/**
 * Wrapper del SDK de RevenueCat (`@revenuecat/purchases-capacitor`) para las
 * compras in-app (speak Fase 23, monetización (b)). RevenueCat es sólo la capa
 * de compra/validación en la store; el ledger de créditos lo acredita NUESTRO
 * backend (`/api/iap/sync`) tras la compra.
 *
 * Reglas de aislamiento:
 *   - Sólo opera en NATIVO (Capacitor). En web `isPurchaseAvailable()` es false
 *     y nada del SDK se invoca.
 *   - El SDK se carga por `import()` dinámico → NO entra al chunk principal del
 *     bundle web.
 *   - Sin la public SDK key de la plataforma (VITE_REVENUECAT_IOS_KEY en iOS,
 *     VITE_REVENUECAT_ANDROID_KEY en Android; hasta que el dueño cree la app en
 *     RevenueCat), la compra no está disponible (la UI muestra "próximamente").
 *
 * ⚠️ Este módulo no se puede testear en CI (requiere device + cuenta RevenueCat
 * + productos en Play Console). Validar en device durante el setup de v2.
 */
import { isNative, nativePlatform } from '../lib/platform.js';

/**
 * Public SDK keys de RevenueCat, una por plataforma (horneadas al build nativo,
 * NO secretas). RevenueCat exige la key de la plataforma correcta: la de Android
 * (`goog_…`) no configura en iOS y la de iOS (`appl_…`) no configura en Android.
 * La key SECRETA (server) vive en el `.env` del backend
 * (`REVENUECAT_SECRET_API_KEY`), nunca acá.
 */
const ANDROID_KEY = import.meta.env.VITE_REVENUECAT_ANDROID_KEY as string | undefined;
const IOS_KEY = import.meta.env.VITE_REVENUECAT_IOS_KEY as string | undefined;

/** La public SDK key de la plataforma nativa actual, o `undefined` en web / sin configurar. */
function platformKey(): string | undefined {
  switch (nativePlatform()) {
    case 'ios':
      return IOS_KEY;
    case 'android':
      return ANDROID_KEY;
    default:
      return undefined;
  }
}

/** `true` si la compra in-app está disponible (nativo + key de la plataforma configurada). */
export function isPurchaseAvailable(): boolean {
  const key = platformKey();
  return isNative && typeof key === 'string' && key.length > 0;
}

export type PurchaseOutcome = 'completed' | 'cancelled';

// Configuración perezosa: una sola vez por userId (configure al inicio, logIn
// si cambia el usuario). Módulo-level porque el SDK es un singleton nativo.
let configuredForUser: string | null = null;

async function ensureConfigured(userId: string): Promise<void> {
  const apiKey = platformKey();
  if (!apiKey) {
    throw new Error(
      'RevenueCat no configurado: falta la public SDK key de la plataforma ' +
        '(VITE_REVENUECAT_IOS_KEY en iOS / VITE_REVENUECAT_ANDROID_KEY en Android).',
    );
  }
  const { Purchases } = await import('@revenuecat/purchases-capacitor');
  if (configuredForUser === userId) return;
  if (configuredForUser === null) {
    await Purchases.configure({ apiKey, appUserID: userId });
  } else {
    await Purchases.logIn({ appUserID: userId });
  }
  configuredForUser = userId;
}

/**
 * Dispara la compra de un pack en la store. Resuelve 'completed' si la compra se
 * concretó (RevenueCat ya la validó) o 'cancelled' si el usuario canceló. Lanza
 * ante errores reales (red, producto inexistente, etc.). NO acredita créditos —
 * eso lo hace el caller llamando a `/api/iap/sync` cuando esto resuelve
 * 'completed'.
 */
export async function purchaseCreditPack(
  productId: string,
  userId: string,
): Promise<PurchaseOutcome> {
  if (!isPurchaseAvailable()) {
    throw new Error('La compra in-app no está disponible en esta plataforma.');
  }
  const { Purchases, PURCHASES_ERROR_CODE, PRODUCT_CATEGORY } = await import(
    '@revenuecat/purchases-capacitor'
  );
  await ensureConfigured(userId);

  try {
    // Camino preferido: el Offering configurado en RevenueCat trae los packages.
    const offerings = await Purchases.getOfferings();
    const pkg = offerings.current?.availablePackages.find(
      (p) => p.product.identifier === productId,
    );
    if (pkg) {
      await Purchases.purchasePackage({ aPackage: pkg });
    } else {
      // Fallback: sin Offering configurado, compramos el producto directo.
      const { products } = await Purchases.getProducts({
        productIdentifiers: [productId],
        type: PRODUCT_CATEGORY.NON_SUBSCRIPTION,
      });
      const product = products.find((p) => p.identifier === productId);
      if (!product) {
        throw new Error(`El producto ${productId} no está disponible en la store.`);
      }
      await Purchases.purchaseStoreProduct({ product });
    }
    return 'completed';
  } catch (err) {
    if (
      err !== null &&
      typeof err === 'object' &&
      'code' in err &&
      (err as { code?: unknown }).code === PURCHASES_ERROR_CODE.PURCHASE_CANCELLED_ERROR
    ) {
      return 'cancelled';
    }
    throw err;
  }
}

/**
 * Best-effort: precios localizados de la store por SKU (para mostrar el precio
 * real que verá el usuario en vez del `priceUsd` de referencia). Si falla o no
 * hay productos, devuelve un mapa vacío y la UI cae al `priceUsd` del backend.
 */
export async function fetchLocalizedPrices(
  userId: string,
  productIds: string[],
): Promise<Map<string, string>> {
  const out = new Map<string, string>();
  if (!isPurchaseAvailable() || productIds.length === 0) return out;
  try {
    const { Purchases, PRODUCT_CATEGORY } = await import('@revenuecat/purchases-capacitor');
    await ensureConfigured(userId);
    const { products } = await Purchases.getProducts({
      productIdentifiers: productIds,
      type: PRODUCT_CATEGORY.NON_SUBSCRIPTION,
    });
    for (const p of products) out.set(p.identifier, p.priceString);
  } catch {
    // Best-effort: ignoramos y dejamos que la UI use el precio de referencia.
  }
  return out;
}
